"use client"

import {
    Accordion,
    AccordionTrigger,
    AccordionContent,
    AccordionItem,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import {
    FiltersAccordionsEnum,
    FiltersAccordionsState,
} from "@/lib/cookies/client-cookies"
import { ProductsSearchDataResponse } from "@repo/api-shared"
import { useCallback, useEffect, useState } from "react"
import debounce from "lodash/debounce"

export function FiltersBlock({
    filters,
    filtersAccordionsState,
    toggleSection,
    searchQuery,
    onSearchQueryChange,
    selectedPriceRange,
    minPrice,
    maxPrice,
    onMinPriceChange,
    onMaxPriceChange,
    selectedCategories,
    onCategoryToggle,
    selectedSizes,
    onSizeToggle,
    selectedTags,
    onTagToggle,
    onSubmit,
    resetFilters,
}: {
    filters: ProductsSearchDataResponse["filters"] | null | undefined
    filtersAccordionsState: FiltersAccordionsState
    toggleSection: (sectionId: keyof FiltersAccordionsState) => void
    searchQuery: string
    onSearchQueryChange: (query: string) => void
    selectedPriceRange: [number, number]
    minPrice: number
    maxPrice: number
    onMinPriceChange: (minPrice: number) => void
    onMaxPriceChange: (maxPrice: number) => void
    selectedCategories: string[]
    onCategoryToggle: (categoryId: string) => void
    selectedSizes: string[]
    onSizeToggle: (sizeId: string) => void
    selectedTags: string[]
    onTagToggle: (tagId: string) => void
    onSubmit: () => void
    resetFilters: () => void
}) {
    const [isClient, setIsClient] = useState(false)

    useEffect(() => {
        setIsClient(true)
    }, [])

    // Local state for immediate input updates
    const [localPriceRange, setLocalPriceRange] = useState(selectedPriceRange)

    useEffect(() => {
        setLocalPriceRange(selectedPriceRange)
    }, [selectedPriceRange])

    const debouncedPriceChange = useCallback(
        debounce(([min, max]: [number, number]) => {
            onMinPriceChange(min || 0)
            onMaxPriceChange(max || 0)
        }, 100),
        [onMinPriceChange, onMaxPriceChange],
    )

    // Handler for immediate visual updates
    const handlePriceChange = useCallback(
        ([min, max]: [number, number]) => {
            setLocalPriceRange([min, max])
            debouncedPriceChange([min, max])
        },
        [debouncedPriceChange],
    )

    return (
        <div className="border border-gray-300 p-4">
            <h2 className="mb-4 pb-2 text-xl font-bold">Поиск</h2>

            <Input
                type="search"
                placeholder="Поиск"
                className="mb-4"
                value={searchQuery}
                onChange={(e) => {
                    onSearchQueryChange(e.target.value)
                }}
            />

            <div className="flex flex-col gap-2">
                <Button
                    onClick={onSubmit}
                    className="w-full bg-primary hover:bg-primary/80"
                >
                    Применить
                </Button>
            </div>

            <div className="mb-6 space-y-4">
                <Accordion
                    type="multiple"
                    onValueChange={(newOpenSections) => {
                        // Get currently open sections from state
                        const currentOpenSections = Object.keys(
                            filtersAccordionsState,
                        ).filter(
                            (key) =>
                                filtersAccordionsState[
                                    key as keyof FiltersAccordionsState
                                ],
                        )

                        // Find which section changed by comparing arrays
                        const changedSection =
                            currentOpenSections.find(
                                (section) => !newOpenSections.includes(section),
                            ) ||
                            newOpenSections.find(
                                (section) =>
                                    !currentOpenSections.includes(section),
                            )

                        if (changedSection) {
                            toggleSection(
                                changedSection as keyof FiltersAccordionsState,
                            )
                        }
                    }}
                    defaultValue={Object.keys(filtersAccordionsState).filter(
                        (key) =>
                            filtersAccordionsState[
                                key as keyof FiltersAccordionsState
                            ],
                    )}
                >
                    <AccordionItem value={FiltersAccordionsEnum.PRICE}>
                        <AccordionTrigger>Цена, ₽</AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-4 px-1">
                                {isClient ? (
                                    <>
                                        <Slider
                                            className="mt-2"
                                            value={localPriceRange}
                                            min={minPrice}
                                            max={maxPrice}
                                            step={5000}
                                            onValueChange={handlePriceChange}
                                        />
                                        <div className="flex justify-between gap-2">
                                            <div className="w-full">
                                                <Label
                                                    htmlFor="min-price"
                                                    className="sr-only"
                                                >
                                                    Минимальная цена
                                                </Label>
                                                <Input
                                                    id="min-price"
                                                    type="number"
                                                    value={localPriceRange[0]}
                                                    onChange={(e) => {
                                                        const newMin =
                                                            Number.parseInt(
                                                                e.target.value,
                                                            )
                                                        handlePriceChange([
                                                            newMin,
                                                            localPriceRange[1],
                                                        ])
                                                    }}
                                                    className="w-full"
                                                />
                                            </div>
                                            <span className="self-center">
                                                —
                                            </span>
                                            <div className="w-full">
                                                <Label
                                                    htmlFor="max-price"
                                                    className="sr-only"
                                                >
                                                    Максимальная цена
                                                </Label>
                                                <Input
                                                    id="max-price"
                                                    type="number"
                                                    value={localPriceRange[1]}
                                                    onChange={(e) => {
                                                        const newMax =
                                                            Number.parseInt(
                                                                e.target.value,
                                                            )
                                                        handlePriceChange([
                                                            localPriceRange[0],
                                                            newMax,
                                                        ])
                                                    }}
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Slider
                                            className="mt-2"
                                            value={[minPrice, maxPrice]}
                                            min={minPrice}
                                            max={maxPrice}
                                            step={5000}
                                            onValueChange={() => {}}
                                            disabled
                                        />
                                        <div className="flex justify-between gap-2">
                                            <div className="w-full">
                                                <Label
                                                    htmlFor="min-price"
                                                    className="sr-only"
                                                >
                                                    Минимальная цена
                                                </Label>
                                                <Input
                                                    id="min-price"
                                                    type="number"
                                                    value={minPrice}
                                                    onChange={() => {}}
                                                    className="w-full"
                                                    disabled
                                                />
                                            </div>
                                            <span className="self-center">
                                                —
                                            </span>
                                            <div className="w-full">
                                                <Label
                                                    htmlFor="max-price"
                                                    className="sr-only"
                                                >
                                                    Максимальная цена
                                                </Label>
                                                <Input
                                                    id="max-price"
                                                    type="number"
                                                    value={maxPrice}
                                                    onChange={() => {}}
                                                    className="w-full"
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value={FiltersAccordionsEnum.CATEGORIES}>
                        <AccordionTrigger>Категория</AccordionTrigger>
                        <AccordionContent>
                            <div>
                                {filters &&
                                    Object.entries(filters?.categories).map(
                                        ([categoryName, { count }]) => (
                                            <Label
                                                key={categoryName}
                                                htmlFor={`category-${categoryName}`}
                                                className="flex w-full cursor-pointer items-center space-x-2 py-1 hover:opacity-80"
                                            >
                                                <Checkbox
                                                    id={`category-${categoryName}`}
                                                    checked={selectedCategories.includes(
                                                        categoryName,
                                                    )}
                                                    onCheckedChange={() =>
                                                        onCategoryToggle(
                                                            categoryName,
                                                        )
                                                    }
                                                />
                                                <span>{categoryName}</span>
                                                {/* <span className="text-sm text-gray-500">
                                                    {count}
                                                </span> */}
                                            </Label>
                                        ),
                                    )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value={FiltersAccordionsEnum.SIZES}>
                        <AccordionTrigger>Размер</AccordionTrigger>
                        <AccordionContent>
                            <div>
                                {filters &&
                                    Object.entries(filters?.sizes).map(
                                        ([sizeName, { count }]) => (
                                            <Label
                                                key={sizeName}
                                                htmlFor={`size-${sizeName}`}
                                                className="flex w-full cursor-pointer items-center space-x-2 py-1 hover:opacity-80"
                                            >
                                                <Checkbox
                                                    id={`size-${sizeName}`}
                                                    checked={selectedSizes.includes(
                                                        sizeName,
                                                    )}
                                                    onCheckedChange={() =>
                                                        onSizeToggle(sizeName)
                                                    }
                                                />
                                                <span>{sizeName}</span>
                                                {count ? (
                                                    <span className="text-sm text-gray-500">
                                                        {count}
                                                    </span>
                                                ) : null}
                                            </Label>
                                        ),
                                    )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value={FiltersAccordionsEnum.TAGS}>
                        <AccordionTrigger>Пометки</AccordionTrigger>
                        <AccordionContent>
                            <div>
                                {filters &&
                                    Object.entries(filters?.tags).map(
                                        ([tagName, { count }]) => (
                                            <Label
                                                key={tagName}
                                                htmlFor={`tag-${tagName}`}
                                                className="flex w-full cursor-pointer items-center space-x-2 py-1 hover:opacity-80"
                                            >
                                                <Checkbox
                                                    id={`tag-${tagName}`}
                                                    checked={selectedTags.includes(
                                                        tagName,
                                                    )}
                                                    onCheckedChange={() =>
                                                        onTagToggle(tagName)
                                                    }
                                                />
                                                <span>{tagName}</span>
                                                {count ? (
                                                    <span className="text-sm text-gray-500">
                                                        {count}
                                                    </span>
                                                ) : null}
                                            </Label>
                                        ),
                                    )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
            <div className="flex flex-col gap-2">
                <Button
                    onClick={onSubmit}
                    className="w-full bg-primary hover:bg-primary/80"
                >
                    Применить
                </Button>
                <Button
                    onClick={resetFilters}
                    variant="outline"
                    className="w-full border-primary text-primary hover:bg-primary/10"
                >
                    Сбросить
                </Button>
            </div>
        </div>
    )
}
